/* eslint-disable no-nested-ternary */
import { ScopedCssBaseline, styled } from "@mui/material";
import { Suspense, useMemo, useRef } from "react";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { authRoutes, universalRoutes } from "modules/auth/routes/auth";
import { selectCurrentUser } from "modules/auth/store/auth.slice";
import { Spinner } from "modules/common/components/Spinner";
import { Page403 } from "pages/Page403";
import { appRoutes } from "routes";
import { SentryRoute } from "sentry";

import { useAbility } from "./useAbility";

import type { ReactNode } from "react";

import type { AppAbilityType } from "context/ability/ability";
import type { Route as RouteModel, RouteCan } from "routes/RouteModel";

const flattenRoutes = (routes: RouteModel[]) => {
  const flattened: RouteModel[] = [];

  routes.forEach((route) => {
    if (route.children && route.children.length > 0) {
      flattened.push(...flattenRoutes(route.children));
    }

    flattened.push(route);
  });

  return flattened;
};

interface WrapperProps {
  children: ReactNode;
}

const Wrapper = ({ children }: WrapperProps) => (
  <FullWidthScopedCssBaseline>{children}</FullWidthScopedCssBaseline>
);

const FullWidthScopedCssBaseline = styled(ScopedCssBaseline)({
  background: "transparent",
  width: "inherit",
});

interface AbilityComponentProps {
  Component: RouteModel["component"];
  can: RouteModel["can"];
  props: NonNullable<unknown>;
}

interface AuthGateProps {
  can: RouteModel["can"];
  children: ReactNode;
}

function useCurrentUser(suspend = true) {
  const user = useSelector(selectCurrentUser);

  const pendingRef = useRef<Promise<never> | null>(null);

  if (suspend) {
    if (!user?.id) {
      if (!pendingRef.current) {
        pendingRef.current = new Promise<never>(() => {});
      }

      // trigger suspend
      throw pendingRef.current;
    }

    if (pendingRef.current) {
      pendingRef.current = null;
    }
  }

  return user;
}

function AuthGate({ can, children }: AuthGateProps) {
  const ability = useAbility();
  const user = useCurrentUser(Boolean(can));
  const accessPass = processRouteCan(can, ability);

  if (can && user?.id && !accessPass) {
    return <Page403 />;
  }

  return children;
}

function AbilityComponent({ can, Component, props }: AbilityComponentProps) {
  return (
    <Suspense fallback={<Spinner />}>
      <AuthGate can={can}>
        {Component ? <Component {...props} /> : null}
      </AuthGate>
    </Suspense>
  );
}

export function processRouteCan(
  can: RouteModel["can"],
  ability: AppAbilityType
) {
  if (!can) {
    return false;
  }

  let tests = isCanOr(can) ? [...can.or] : [can];

  let pass = false;
  for (let test of tests) {
    pass = pass || ability.can(test.do, test.on, test.field);
  }

  return pass;
}

function isCanOr(can: RouteModel["can"]): can is { or: RouteCan[] } {
  return can !== null && typeof can === "object" && "or" in can;
}

export const useBuildChildRoutes = (
  routes: RouteModel[],
  ability?: AppAbilityType
) => {
  const { t } = useTranslation("common");

  return useMemo(
    () =>
      flattenRoutes(routes)
        .filter(({ path }) => path)
        .map((route) => {
          if (
            route.children?.length &&
            route.showChildrenInSidebar &&
            ability
          ) {
            const firstPossibleToViewRoute =
              route.children.find(
                ({ can }) => !can || processRouteCan(can, ability)
              ) ?? route.children[0];

            return {
              ...route,
              component: () => <Redirect to={firstPossibleToViewRoute.path} />,
            };
          }

          return route;
        })
        .map(
          ({
            path,
            can = null,
            hasNestedSwitch = false,
            component: Component,
            title,
          }) => (
            <SentryRoute
              key={path}
              exact={!hasNestedSwitch}
              path={path}
              render={(props: NonNullable<unknown>) => (
                <Wrapper>
                  {title && (
                    <Helmet
                      defer={false}
                      title={(Array.isArray(title) ? title : [title])
                        .map((it) => t(it))
                        .join(" • ")}
                    />
                  )}
                  <AbilityComponent
                    Component={Component}
                    can={can}
                    props={props}
                  />
                </Wrapper>
              )}
            />
          )
        ),
    [ability, routes, t]
  );
};

export function useAppRoutes(
  appRoutesFilter?: (routes: RouteModel[]) => RouteModel[]
) {
  const ability = useAbility();
  const switchAuthRoutes = useBuildChildRoutes(authRoutes);
  const switchUniversalRoutes = useBuildChildRoutes(universalRoutes);
  const switchAppRoutes = useBuildChildRoutes(appRoutes, ability);

  const filteredValues = useMemo(() => {
    const isAppRoutesFilterProvided = typeof appRoutesFilter === "function";

    if (isAppRoutesFilterProvided) {
      const filteredAppRoutes = appRoutesFilter(appRoutes);
      const filteredAppPaths = filteredAppRoutes.map((route) => route.path);

      return {
        filteredAppRoutes,
        filteredAppPaths,
      };
    }

    return {};
  }, [appRoutesFilter]);

  const authPaths = authRoutes.map((route) => route.path);
  const appPaths = appRoutes.map((route) => route.path);
  const universalPaths = universalRoutes.map((route) => route.path);

  const allRoutes = [...switchAppRoutes, ...switchAuthRoutes].map((route) =>
    String(route.key)
  );

  return {
    switchAuthRoutes,
    switchAppRoutes,
    authPaths,
    appPaths,
    allRoutes,
    universalPaths,
    switchUniversalRoutes,
    ...filteredValues,
  };
}
